import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { BACKEND_URL } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';

const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: searchParams.get('role') === 'organiser' ? 'organiser' : 'customer',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    const result = await register(formData.name, formData.email, formData.password, formData.role);

    if (result.success) {
      // If the user came from "Notify Me at Launch" (?notify=1), auto-opt
      // them in before we redirect them back to wherever they came from.
      const fromNotifyCta = searchParams.get('notify') === '1';
      const nextPath = searchParams.get('next');
      if (fromNotifyCta) {
        try {
          await axios.post(
            `${BACKEND_URL}/api/notify-me`,
            { source: 'register_post_launch_cta' },
            { withCredentials: true },
          );
          toast.success("You're on the list — we'll notify you when bookings open.");
        } catch {
          toast.success('Registration successful!');
        }
      } else {
        toast.success('Registration successful!');
      }

      if (nextPath) {
        navigate(decodeURIComponent(nextPath));
      } else {
        navigate(formData.role === 'organiser' ? '/dashboard' : '/');
      }
    } else {
      toast.error(result.error || 'Registration failed');
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-[#E5E9E6]">
          <div className="text-center mb-8">
            <h1
              className="text-4xl font-bold text-[#112318] mb-2"
              style={{ fontFamily: 'Playfair Display, serif' }}
              data-testid="register-title"
            >
              Join VinePass
            </h1>
            <p className="text-[#4A5D50]">Create your account to get started</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="name" className="text-[#4A5D50]">Full Name</Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="mt-1"
                data-testid="register-name-input"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-[#4A5D50]">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="mt-1"
                data-testid="register-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-[#4A5D50]">Password</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={8}
                className="mt-1"
                data-testid="register-password-input"
              />
              <p className="text-xs text-[#4A5D50] mt-1">
                Must be 8+ characters with uppercase, lowercase, and number
              </p>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-[#4A5D50]">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
                minLength={6}
                className="mt-1"
                data-testid="register-confirm-password-input"
              />
            </div>

            <div>
              <Label className="text-[#4A5D50] mb-3 block">Account Type</Label>
              <RadioGroup
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
                data-testid="register-role-selection"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="customer" id="customer" />
                  <Label htmlFor="customer" className="cursor-pointer">Customer - I want to buy tickets</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="organiser" id="organiser" />
                  <Label htmlFor="organiser" className="cursor-pointer">Organiser - I want to list events</Label>
                </div>
              </RadioGroup>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full py-6 font-medium transition-all shadow-md text-lg"
              data-testid="register-submit-button"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-[#4A5D50]">
              Already have an account?{' '}
              <Link to="/login" className="text-[#1F3D2B] font-semibold hover:underline" data-testid="login-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;